import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../api/client";

type Props = {
  onClose: () => void;
};

function parseCsvRows(text: string): string[][] {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const rows: string[][] = [];
  for (const line of lines) {
    if (!line.trim()) continue;
    rows.push(line.split(",").map((c) => c.trim()));
  }
  return rows;
}

export function TrainSampleDataModal({ onClose }: Props) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch("/api/train/sample-csv")
      .then(async (res) => {
        const ct = res.headers.get("content-type") ?? "";
        if (!res.ok) {
          const errBody = ct.includes("json") ? await res.json().catch(() => ({})) : {};
          const detail =
            typeof (errBody as { message?: string }).message === "string"
              ? (errBody as { message: string }).message
              : typeof (errBody as { error?: string }).error === "string"
                ? (errBody as { error: string }).error
                : `Could not load sample (HTTP ${res.status}).`;
          throw new Error(detail);
        }
        if (ct.includes("html")) {
          throw new Error("Sample URL returned HTML instead of CSV.");
        }
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const parsed = parseCsvRows(text);
        if (parsed.length < 2) {
          throw new Error("Sample file is empty or invalid.");
        }
        setRows(parsed);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load sample data.");
          setRows(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const header = rows?.[0] ?? [];
  const bodyRows = rows?.slice(1) ?? [];

  return createPortal(
    <div
      className="model-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="model-modal train-sample-data-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="model-modal-header">
          <div>
            <p className="eyebrow">Sample</p>
            <h2 id={titleId} className="model-modal-title">Example shirt sales data</h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="model-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="model-modal-body train-sample-data-body">
          {loading && <p className="muted page-loading">Loading sample data…</p>}
          {error && !loading && (
            <p className="model-run-error" role="alert">{error}</p>
          )}
          {!loading && !error && rows && (
            <div className="train-sample-table-wrap">
              <table className="train-sample-table">
                <thead>
                  <tr>
                    {header.map((col, i) => (
                      <th key={`${col}-${i}`}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, ri) => (
                    <tr key={ri}>
                      {header.map((_, ci) => (
                        <td key={ci}>{row[ci] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
